import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Body2 } from "@activecollab/components";
import PropTypes from "prop-types";
import BalanceTooltipContent from "~/components/Subscription/SubscriptionPlanDetails/BalanceTooltipContent";
import PriceTooltipContent from "~/components/Subscription/SubscriptionPlanDetails/PriceTooltipContent";
import StorageUsage from "~/components/Subscription/SubscriptionPlanDetails/StorageUsage";
import SubscriptionInfoIconWithTooltip from "~/components/Subscription/SubscriptionPlanDetails/SubscriptionInfoIconWithTooltip";
import { getRecordedBalance } from "~/store/selectors/selector.accountBalance";
import { getAddOnsAsString } from "~/store/selectors/selector.accountSettings";
import { getPriceBreakdown } from "~/store/selectors/selector.subscriptionBrakeDownPrice";
import { roundAmount } from "~/utils/utils";

const PerSeatPlanDetails = ({ planInfo, storageUsage, balanceTotal, isCanceled }) => {
  const { t } = useTranslation();
  const addOnNames = useSelector((state) => getAddOnsAsString(state));
  const balanceRecords = useSelector((state) => getRecordedBalance(state));
  const priceBreakDown = useSelector((state) => getPriceBreakdown(state));

  return (
    <>
      <Body2
        color="primary"
        weight="bold"
        className="tw-mr-8"
      >
        {t("Plan")}
      </Body2>
      <Body2
        color="tertiary"
        data-testid="billingPeriod"
      >
        {planInfo?.billingPeriod}
      </Body2>
      <>
        <Body2
          color="primary"
          weight="bold"
          className="tw-mr-8"
        >
          {t("Additional Bundles")}
        </Body2>
        <div
          className="tw-flex"
          data-testid="additionalBundles"
        >
          {addOnNames.length > 0 ? (
            <Body2
              color="tertiary"
              className="tw-mr-2"
            >
              {addOnNames}
            </Body2>
          ) : isCanceled ? (
            <Body2 color="tertiary">-</Body2>
          ) : null}
          {!isCanceled ? (
            <Body2 color="theme-primary">
              <Link
                to="/subscription/manage-add-ons"
                className="activity-link"
              >
                {t("Manage Bundles")}
              </Link>
            </Body2>
          ) : null}
        </div>
      </>
      <Body2
        color="primary"
        weight="bold"
        className="tw-mr-8"
      >
        {t("Price")}
      </Body2>
      <div className="tw-flex tw-items-center">
        <Body2
          color="tertiary"
          className="tw-mr-2"
          data-testid="priceLabel"
        >
          ${priceBreakDown?.total}
        </Body2>
        <SubscriptionInfoIconWithTooltip title={<PriceTooltipContent />} />
      </div>
      <Body2
        color="primary"
        weight="bold"
        className="tw-mr-8"
      >
        {t("Team Members")}
      </Body2>
      <Body2
        color="tertiary"
        data-testid="teamMembers"
      >
        {planInfo?.members}
      </Body2>

      <Body2
        color="primary"
        weight="bold"
        className="tw-mr-8"
      >
        {t("Storage Usage")}
      </Body2>
      <div className="tw-flex tw-items-center">
        <StorageUsage
          storage={planInfo.storage}
          storageUsage={storageUsage}
        />
        {!isCanceled ? (
          <Body2 data-testid="manageStorage">
            <Link
              to="/subscription/manage-add-ons/storage"
              className="tw-ml-2 activity-link"
            >
              {t("Manage Storage")}
            </Link>
          </Body2>
        ) : null}
      </div>

      {!planInfo.is_suspended && planInfo.is_activated ? (
        <>
          <Body2
            color="primary"
            weight="bold"
            className="tw-mr-8"
            data-testid="nextBillingDateLabel"
          >
            {planInfo?.nextBillDateLabel}
          </Body2>
          <Body2
            color="tertiary"
            data-testid="nextBillingDateValue"
          >
            {planInfo?.nextBillDateValue}
          </Body2>
        </>
      ) : null}

      {Number(balanceTotal) !== 0 ? (
        <>
          <Body2
            color="primary"
            weight="bold"
            className="tw-mr-8"
            data-testid="balanceTotalLabel"
          >
            {t("Balance")}
          </Body2>
          <div className="tw-flex tw-items-center">
            <Body2
              color="tertiary"
              className="tw-mr-2"
              data-testid="balanceTotalValue"
            >
              ${roundAmount(balanceRecords)}
            </Body2>
            <SubscriptionInfoIconWithTooltip
              title={<BalanceTooltipContent />}
              wide
            />
          </div>
        </>
      ) : null}
    </>
  );
};

PerSeatPlanDetails.propTypes = {
  planInfo: PropTypes.object,
  storageUsage: PropTypes.object,
  balanceTotal: PropTypes.number,
  isCanceled: PropTypes.bool,
};

export default PerSeatPlanDetails;

PerSeatPlanDetails.displayName = "PerSeatPlanDetails";
